// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { ActionIcon, Badge, Button, CopyButton, Group, Stack, Text, Title, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createReference, formatHumanName } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { getMrn, isPendingIntakePatient, isTestPatient } from '@polarissleep/shared';
import { IconCheck, IconCopy, IconPill, IconUsers } from '@tabler/icons-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { JSX } from 'react';
import { useState } from 'react';
import { SelfPayBadge } from '../../../components/SelfPayBadge';
import { SEX_AT_BIRTH_OPTIONS } from '../../../constants/patient-profile';
import { botIdentifier } from '../../../lib/bot-identifier';
import { showErrorNotification } from '../../../utils/notifications';
import { getBirthSex } from '../../../utils/patient-extensions';
import classes from './PatientChartHeader.module.css';
import { PatientStatusBar } from './PatientStatusBar';

dayjs.extend(relativeTime);

function formatLastActive(lastUpdated: string | undefined): string {
  if (!lastUpdated) {
    return '—';
  }
  return dayjs(lastUpdated).fromNow();
}

export interface PatientChartHeaderProps {
  patient: Patient;
  onUpdated?: (updated: Patient) => void;
  onSwitchPatient?: () => void;
  onPrescribe?: () => void;
}

export function PatientChartHeader({
  patient,
  onUpdated,
  onSwitchPatient,
  onPrescribe,
}: PatientChartHeaderProps): JSX.Element {
  const medplum = useMedplum();
  const [inviting, setInviting] = useState(false);
  const name = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unnamed patient';
  const lastActive = formatLastActive(patient.meta?.lastUpdated);
  const mrn = getMrn(patient);
  const age = patient.birthDate ? dayjs().diff(dayjs(patient.birthDate), 'year') : undefined;
  const birthSex = getBirthSex(patient);
  const gender =
    SEX_AT_BIRTH_OPTIONS.find((option) => option.value === birthSex)?.label ??
    (patient.gender ? `${patient.gender[0].toUpperCase()}${patient.gender.slice(1)}` : undefined);

  const sendWelcomeInvite = async (): Promise<void> => {
    setInviting(true);
    try {
      await medplum.executeBot(botIdentifier('invite-patient'), { patientReference: createReference(patient) });
      notifications.show({ color: 'green', message: 'Welcome invite sent.' });
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className={classes.header}>
      {onSwitchPatient && (
        <Button
          fullWidth
          variant="light"
          size="sm"
          leftSection={<IconUsers size={16} />}
          onClick={onSwitchPatient}
          className={classes.switchPatientButton}
        >
          Switch Patient
        </Button>
      )}
      <Group align="flex-start" justify="space-between" wrap="wrap" gap="xl">
        <Stack gap={4} className={classes.identity}>
          <Group gap={8} align="center">
            <Title order={1} className={classes.name}>
              {name}
            </Title>
            {isTestPatient(patient) && (
              <Badge color="orange" variant="light">
                Test Patient
              </Badge>
            )}
            <SelfPayBadge patient={patient} />
            {isPendingIntakePatient(patient) && (
              <Badge color="yellow" variant="light">
                Pending intake
              </Badge>
            )}
          </Group>
          <Stack gap={2}>
            <Text size="sm" c="dimmed">
              {age !== undefined ? `${age} yrs` : 'Age not recorded'}
              {' · '}
              {gender ?? 'Gender not recorded'}
            </Text>
            <Text size="sm" c="dimmed" component="div">
              <Group gap={4} wrap="nowrap" align="center">
                <Text span fw={600} c="dimmed">
                  MRN:
                </Text>
                <span className={classes.mrnValue} aria-label="Patient MRN">
                  {mrn ?? '—'}
                </span>
                {mrn && (
                  <CopyButton value={mrn}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? 'Copied' : 'Copy MRN'} withArrow>
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          color="gray"
                          onClick={copy}
                          aria-label="Copy MRN to clipboard"
                        >
                          {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                )}
              </Group>
            </Text>
            <Text size="sm" c="dimmed">
              <Text span fw={600} c="dimmed">
                Last Active:
              </Text>{' '}
              {lastActive}
            </Text>
          </Stack>
        </Stack>

        <Stack gap="sm" className={classes.status}>
          {onPrescribe && (
            <Button leftSection={<IconPill size={16} />} onClick={onPrescribe}>
              Prescribe
            </Button>
          )}
          {isPendingIntakePatient(patient) && (
            <Button variant="light" loading={inviting} onClick={() => sendWelcomeInvite().catch(console.error)}>
              Send welcome invite
            </Button>
          )}
          <PatientStatusBar patient={patient} onUpdated={onUpdated} />
        </Stack>
      </Group>
    </div>
  );
}
